import DOMPurify from 'dompurify';
import { useEffect, useRef } from 'react';

/**
 * 輕量所見即所得編輯器（景點描述 F-3 用）。
 *
 * 設計：以 contentEditable + document.execCommand 實作（無重型編輯器套件），
 * 輸出經 DOMPurify 淨化的 HTML（白名單僅保留格式標籤）。父層以 `key` 控制
 * 切換語言時重新掛載，避免 controlled contentEditable 的游標跳動問題。
 *
 * 安全：本元件 onChange 已淨化；消費端（kiosk/PWA）渲染時仍須再次以
 * DOMPurify 淨化後才 dangerouslySetInnerHTML（防直接打 API 寫入之惡意 HTML）。
 */

const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'ul', 'ol', 'li', 'a', 'span'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

/** 白名單淨化 rich-text HTML（僅留格式標籤 + a[href]）。 */
export function sanitizeRichText(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

/** 去標籤後是否為空（空 contentEditable 會殘留 <br> / <p></p>）。 */
export function richTextIsEmpty(html: string): boolean {
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, '');
  return text.length === 0;
}

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export function RichTextEditor({ value, onChange, placeholder }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  // 同步外部 value 進 DOM —— **僅在編輯器未取得焦點時**回寫（避免使用者打字時游標跳動）。
  // 這道處理「非同步載入既有資料」之情境（編輯模式 spotQuery 載入晚於掛載），
  // 同時使用者輸入觸發的 value 變更因焦點仍在編輯器內而被略過，不影響打字。
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== value) {
      el.innerHTML = value;
    }
  }, [value]);

  const emit = (): void => {
    if (ref.current) onChange(sanitizeRichText(ref.current.innerHTML));
  };

  const exec = (command: string, arg?: string): void => {
    ref.current?.focus();
    // execCommand 已標記 deprecated，但仍為各瀏覽器可用之零依賴 rich-text 編輯途徑
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = (): void => {
    const url = window.prompt('連結網址（https://…）')?.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      window.alert('僅接受 http(s) 連結');
      return;
    }
    exec('createLink', url);
  };

  return (
    <div style={wrap}>
      <style>{RTE_CSS}</style>
      <div style={toolbar}>
        <ToolBtn label="粗體" onClick={() => exec('bold')}>
          <b>B</b>
        </ToolBtn>
        <ToolBtn label="斜體" onClick={() => exec('italic')}>
          <i>I</i>
        </ToolBtn>
        <ToolBtn label="底線" onClick={() => exec('underline')}>
          <u>U</u>
        </ToolBtn>
        <span style={sep} />
        <ToolBtn label="項目清單" onClick={() => exec('insertUnorderedList')}>
          • 清單
        </ToolBtn>
        <ToolBtn label="編號清單" onClick={() => exec('insertOrderedList')}>
          1. 清單
        </ToolBtn>
        <span style={sep} />
        <ToolBtn label="插入連結" onClick={addLink}>
          🔗 連結
        </ToolBtn>
        <ToolBtn label="清除格式" onClick={() => exec('removeFormat')}>
          清除格式
        </ToolBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder ?? ''}
        className="rte-editable"
      />
    </div>
  );
}

function ToolBtn({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      // onMouseDown preventDefault：避免按鈕搶走 contentEditable 的選取焦點
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={btn}
    >
      {children}
    </button>
  );
}

const wrap: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: 6,
  overflow: 'hidden',
  background: '#fff',
};
const toolbar: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: 6,
  borderBottom: '1px solid #e5e7eb',
  background: '#f9fafb',
  flexWrap: 'wrap',
};
const btn: React.CSSProperties = {
  minWidth: 30,
  height: 28,
  padding: '0 8px',
  fontSize: 13,
  border: '1px solid #d1d5db',
  borderRadius: 4,
  background: '#fff',
  cursor: 'pointer',
};
const sep: React.CSSProperties = {
  width: 1,
  height: 18,
  background: '#e5e7eb',
  margin: '0 2px',
};

const RTE_CSS = `
.rte-editable { min-height: 130px; padding: 10px; outline: none; font-size: 14px; line-height: 1.6; }
.rte-editable:empty:before { content: attr(data-placeholder); color: #9ca3af; }
.rte-editable:focus { background: #fffef9; }
.rte-editable ul, .rte-editable ol { padding-left: 22px; margin: 6px 0; }
.rte-editable a { color: #2563eb; text-decoration: underline; }
`;
